import * as React from 'react';
import * as style from './Header.css';
import { scrollToAnchor, throttle } from '@cian/newbuilding-utils';

import { getCurrentDocHeight, collectClauses, getActiveTab, mapClausesToTabs } from './helpers';
import { NavigationLink, INavigationLinkProps } from './parts';
import { ITabData } from './types';
import { trackStickyHeaderTabClick } from '../../analytics/trackStickyHeaderTabClick';
import { STICKY_HEADER_HEIGHT } from '../../constants/elements';
import { FavoriteButtonContainer } from '../../containers/FavoriteButton';
import { HeaderRatingStat } from '../../containers/NewbuildingReviewsStat';
import { getCurrentScrollOffset } from '../../utils/dom/getCurrentScrollOffset';
import { ArticleHeading2 } from '@cian/ui-kit';

export interface IHeaderProps {
  name: string;
  actionButton: React.ReactNode;
  messageButton?: React.ReactNode;
}

export interface IHeaderState {
  activeTab: string;
  tabs: ITabData[];
}

export class Header extends React.Component<IHeaderProps, IHeaderState> {
  private lastDocumentHeight: number;

  public constructor(props: IHeaderProps) {
    super(props);

    this.state = {
      activeTab: 'info',
      tabs: [],
    };
  }

  public componentDidMount() {
    this.updateTabData();
    window.addEventListener('resize', this.throttledResizeHandler);
    window.addEventListener('scroll', this.throttledScrollHandler);
  }

  public componentWillUnmount() {
    window.removeEventListener('resize', this.throttledResizeHandler);
    window.removeEventListener('scroll', this.throttledScrollHandler);
  }

  public throttledResizeHandler = throttle(400, () => {
    this.updateTabData();
  });

  public throttledScrollHandler = throttle(200, () => {
    this.checkDocHeight(() => {
      this.updateTabData();
    });
    this.updateActiveTab();
  });

  public updateActiveTab() {
    const { tabs } = this.state;

    if (tabs.length < 1) {
      return;
    }

    const yOffset = getCurrentScrollOffset();
    const activeTab = getActiveTab(yOffset, tabs);

    if (activeTab) {
      this.setState(state => {
        if (state.activeTab !== activeTab.anchor) {
          return { activeTab: activeTab.anchor };
        }

        return null;
      });
    }
  }

  public checkDocHeight(callback?: () => void) {
    if (typeof callback !== 'function') {
      return;
    }

    const currentHeight = getCurrentDocHeight();

    if (this.lastDocumentHeight !== currentHeight) {
      this.lastDocumentHeight = currentHeight;

      callback();
    }
  }

  public updateTabData() {
    const clauses = collectClauses();

    const tabs = mapClausesToTabs(clauses);

    this.setState({ tabs });
  }

  public onNavigationLinkClick(slug: string) {
    return (event: React.MouseEvent<HTMLElement>) => {
      trackStickyHeaderTabClick(slug);
      const anchor = `#${slug}`;

      scrollToAnchor({ anchor, hasRelocation: false })(event);

      if (history.pushState) {
        history.pushState(null, '', anchor);
      } else {
        setTimeout(() => {
          window.location.hash = anchor;
        }, 500);
      }
    };
  }

  public render() {
    const { activeTab, tabs } = this.state;

    const { actionButton, name, messageButton } = this.props;

    return (
      <div style={{ height: `${STICKY_HEADER_HEIGHT}px` }} className={style['header']} data-testid="StickyHeader">
        <div className={style['layout']}>
          <div className={style['content']}>
            <span className={style['title']}>
              <span className={style['rating']}>
                <HeaderRatingStat />
              </span>
              <ArticleHeading2 whiteSpace={'nowrap'} as="span">
                {name}
              </ArticleHeading2>
            </span>
            <div className={style['controls']}>
              {actionButton}
              <div className={style['message-button']}>{messageButton}</div>
              <div className={style['favorite-button']}>
                <FavoriteButtonContainer isHeader={true} />
              </div>
            </div>
          </div>
          <nav className={style['nav']}>
            {tabs.map((clause: INavigationLinkProps) => (
              <NavigationLink
                onClick={this.onNavigationLinkClick(clause.anchor)}
                key={clause.anchor}
                {...clause}
                isAcitve={clause.anchor === activeTab}
              />
            ))}
          </nav>
        </div>
      </div>
    );
  }
}
